/**
 * 3 · Search Console — la única fuente que dice si el trabajo sirvió.
 *
 * La propiedad es del CLIENTE, no nuestra: por eso se conecta acá, por proyecto,
 * y no en Configuración. La de un cliente no es la de otro.
 *
 * Lo que esta pantalla hizo mal (2026-09-06), reportado tres veces:
 *
 *   · El campo "Propiedad" venía vacío y el correo de la cuenta de servicio
 *     estaba justo debajo. Eduardo lo pegó ahí. Ahora el campo viene lleno con
 *     la propiedad que casi seguro es —sale de la dirección del sitio—, se
 *     valida antes de mandar, y el correo vive en un bloque aparte que dice para
 *     qué es: lo que tiene que hacer EL CLIENTE, no lo que hay que escribir acá.
 *   · El error de Google se volcaba como JSON en rojo. El servidor lo traduce.
 *   · Con el correo YA guardado, la pantalla mostraba "Recomprobar" y
 *     "Desconectar" y nada más. Recomprobar volvía a preguntarle a Google lo
 *     mismo, y el camino real —desconectar, y recién ahí ver el campo— no lo
 *     decía nadie. Ahora, mientras no esté conectada, el campo está a la vista,
 *     lleno con la propiedad correcta, y un solo botón la cambia.
 */
import { useState, useEffect } from 'react';
import { normalizarPropiedadCliente } from '../../../lib/searchConsoleProperty.js';
import { useTranslation } from 'react-i18next';
import { KVGrid } from '../../ui/KVGrid.js';
import { KVRow } from '../../ui/KVRow.js';
import { Input } from '../../ui/Input.js';
import { Button } from '../../ui/Button.js';
import { Pill } from '../../PillGroup.js';
import { useToast } from '../../ui/Toast.js';
import {
  useSearchConsole, useConectarSearchConsole, useDesconectarSearchConsole,
} from '../../../hooks/useMantenimiento.js';

const nota = {
  margin: 'var(--evo-space-2) 0 0',
  fontSize: 'var(--evo-fs-tiny)',
  color: 'var(--evo-fg-4)',
  fontFamily: 'var(--evo-font)',
  maxWidth: '70ch',
} as const;

/** El cuerpo, sin tarjeta: vive en un acordeón de la tarjeta de Mantenimiento. */
export function CuerpoSearchConsole({ projectId }: { projectId: number }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data } = useSearchConsole(projectId, true);
  const conectar = useConectarSearchConsole(projectId);
  const desconectar = useDesconectarSearchConsole(projectId);
  const [property, setProperty] = useState('');

  const conexion = data?.conexion ?? null;
  const conectada = conexion?.status === 'ok';
  const invalida = data?.propiedadInvalida ?? null;

  // Con qué viene lleno el campo. Si lo guardado no es una propiedad (el
  // correo), con la sugerida: es la que hay que poner. Si tiene forma pero
  // falló, con lo guardado, para corregirlo a mano. Sin conexión, la sugerida.
  const prellenar = conexion && !invalida ? conexion.property : (data?.sugerida ?? null);
  // Una sola vez, al llegar el dato: si Eduardo lo borró para escribir otra, no
  // se le vuelve a poner encima.
  const [prellenada, setPrellenada] = useState(false);
  useEffect(() => {
    if (!prellenada && prellenar) { setProperty(prellenar); setPrellenada(true); }
  }, [prellenar, prellenada]);

  const validacion = property.trim() === '' ? null : normalizarPropiedadCliente(property);
  const motivoInvalida = validacion && !validacion.ok ? validacion.motivo : null;
  const sinCambio = conexion !== null && !invalida && property.trim() === conexion.property;

  function copiar(texto: string): void {
    navigator.clipboard.writeText(texto).then(
      () => { toast.success(t('projects.maintenance.gsc.copied')); },
      () => { toast.error(t('common.error')); },
    );
  }

  const estado = conectada
    ? { tone: 'cyan' as const, texto: t('projects.maintenance.gsc.connected') }
    : conexion
      ? { tone: 'amber' as const, texto: t('projects.maintenance.gsc.attention') }
      : { tone: 'muted' as const, texto: t('projects.maintenance.gsc.notConnected') };

  return (
    <div>
      <div style={{ marginBottom: 'var(--evo-space-2)' }}>
        <Pill tone={estado.tone} variant="outline" size="xs">{estado.texto}</Pill>
      </div>
      {conexion && (
        <KVGrid>
          <KVRow
            label={t(conectada ? 'projects.maintenance.gsc.property' : 'projects.maintenance.gsc.savedProperty')}
            value={<span style={{ fontFamily: 'var(--evo-font-mono)', fontSize: 'var(--evo-fs-tiny)' }}>{conexion.property}</span>}
            last
          />
        </KVGrid>
      )}

      {/* Mientras no esté conectada, el campo está a la vista: con conexión
          fallida es "cambiar la propiedad", sin conexión es conectar. */}
      {!conectada && (
        <div style={{ marginTop: conexion ? 'var(--evo-space-3)' : 0 }}>
          {conexion && (
            <div style={{
              fontSize: 'var(--evo-fs-label)',
              color: 'var(--evo-fg-4)',
              fontFamily: 'var(--evo-font)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 'var(--evo-space-2)',
            }}>
              {t('projects.maintenance.gsc.changeProperty')}
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input
                value={property}
                onChange={setProperty}
                size="sm"
                fullWidth
                placeholder={t('projects.maintenance.gsc.propertyPlaceholder')}
                {...(motivoInvalida ? { error: t(`projects.maintenance.gsc.invalid.${motivoInvalida}`) } : {})}
              />
            </div>
            <Button
              size="sm"
              tone="cyan"
              disabled={!validacion?.ok || sinCambio}
              loading={conectar.isPending}
              onClick={() => {
                conectar.mutate(property.trim(), {
                  onError: e => { toast.error(e instanceof Error ? e.message : t('common.error')); },
                });
              }}
            >
              {t('projects.maintenance.gsc.connect')}
            </Button>
          </div>
          <p style={nota}>
            {data?.sugerida ? t('projects.maintenance.gsc.suggestedHint') : t('projects.maintenance.gsc.propertyHelp')}
          </p>
        </div>
      )}

      {conexion && (
        <div style={{ display: 'flex', gap: 'var(--evo-space-2)', marginTop: 'var(--evo-space-3)' }}>
          {/* Recomprobar solo si lo guardado tiene forma de propiedad: volver a
              preguntarle a Google por un correo es volver a ver el mismo error. */}
          {!invalida && (
            <Button
              size="sm"
              variant="outline"
              loading={conectar.isPending}
              onClick={() => { conectar.mutate(undefined); }}
            >
              {t('projects.maintenance.gsc.recheck')}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            tone="danger"
            loading={desconectar.isPending}
            onClick={() => { desconectar.mutate(); }}
          >
            {t('projects.maintenance.gsc.disconnect')}
          </Button>
        </div>
      )}

      {/* Qué falta, en una frase accionable. Un estado suelto no le dice a nadie
          qué hacer, y la acción es distinta en cada caso. */}
      {data?.queFalta
        ? <p style={{ ...nota, margin: 'var(--evo-space-3) 0 0', color: 'var(--evo-amber)' }}>{data.queFalta}</p>
        : null}

      {/* Bloque APARTE y con título: es lo que tiene que hacer el cliente en su
          Search Console, no algo que haya que escribir acá. Pegado debajo del
          campo, se leía como el valor que iba en el campo. */}
      {data?.cuentaDeServicio
        ? (
          <div style={{ marginTop: 'var(--evo-space-4)', paddingTop: 'var(--evo-space-3)', borderTop: '1px solid var(--evo-stroke)' }}>
            <div style={{
              fontSize: 'var(--evo-fs-label)',
              color: 'var(--evo-fg-4)',
              fontFamily: 'var(--evo-font)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 'var(--evo-space-2)',
            }}>
              {t('projects.maintenance.gsc.clientStep')}
            </div>
            <KVGrid>
              <KVRow
                label={t('projects.maintenance.gsc.serviceAccount')}
                value={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
                    <span style={{ fontFamily: 'var(--evo-font-mono)', fontSize: 'var(--evo-fs-tiny)' }}>
                      {data.cuentaDeServicio}
                    </span>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => { copiar(data.cuentaDeServicio ?? ''); }}
                    >
                      {t('projects.maintenance.gsc.copyLabel')}
                    </Button>
                  </span>
                }
                last
              />
            </KVGrid>
            <p style={nota}>{t('projects.maintenance.gsc.serviceAccountHelp')}</p>
          </div>
        )
        : null}
    </div>
  );
}
